import type { MigrationRunStatus } from '@modules/Clients/ViewClient/adapters/MigrationApiAdapter';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { UserCard } from '@shared/components/ui/usercard';
import type { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { LegacyDashboardClient, LegacyPaymentProfile } from '../models/LegacyDashboardClient';

const PAYMENT_PROFILE_VARIANT: Record<
  LegacyPaymentProfile,
  'default' | 'secondary' | 'outline' | 'transparent-blue' | 'transparent-orange'
> = {
  cod: 'transparent-orange',
  card: 'transparent-blue',
  mixed: 'secondary',
  other: 'outline',
};

const MIGRATION_STATUS_VARIANT: Record<MigrationRunStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  pending: 'secondary',
  running: 'default',
  paused: 'outline',
  completed: 'default',
  failed: 'destructive',
};

const formatNumber = (value: number, decimals = 0): string =>
  value.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

const formatBool = (value: boolean | null | undefined, t: (key: string) => string): string => {
  if (value === null || value === undefined) return '-';
  return value ? t('table.yes') : t('table.no');
};

const formatPaymentBreakdown = (breakdown: LegacyDashboardClient['payment_breakdown']): string => {
  if (!breakdown) return '-';
  const parts: string[] = [];
  if (breakdown.cod_count !== undefined && breakdown.cod_percentage !== undefined) {
    parts.push(`COD: ${breakdown.cod_count} (${formatNumber(breakdown.cod_percentage, 1)}%)`);
  }
  if (breakdown.card_count !== undefined && breakdown.card_percentage !== undefined) {
    parts.push(`Tarjeta: ${breakdown.card_count} (${formatNumber(breakdown.card_percentage, 1)}%)`);
  }
  return parts.length > 0 ? parts.join(' · ') : '-';
};

export const useAgGridColumns = (onViewSgaProducts?: (client: LegacyDashboardClient) => void) => {
  const { t } = useTranslation('legacyDashboardModule');

  const allColumnDefs = useMemo<ColDef<LegacyDashboardClient>[]>(
    () => [
      {
        colId: 'legacy_team_id',
        field: 'legacy_team_id',
        headerName: t('table.legacy_team_id'),
        sortable: true,
        minWidth: 110,
        cellRenderer: ({ data }: { data: LegacyDashboardClient }) =>
          data ? <span className="text-sm font-medium text-title">{data.legacy_team_id}</span> : null,
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'name',
        field: 'name',
        headerName: t('table.name'),
        sortable: true,
        minWidth: 200,
        flex: 1,
        cellRenderer: ({ data }: { data: LegacyDashboardClient }) =>
          data?.name ? <span className="text-sm font-medium text-title">{data.name}</span> : null,
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'orders_count',
        field: 'orders_count',
        headerName: t('table.orders_count'),
        sortable: true,
        minWidth: 130,
        cellRenderer: ({ data }: { data: LegacyDashboardClient }) =>
          data ? <span className="text-sm text-title">{formatNumber(data.orders_count)}</span> : null,
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'avg_orders_per_month',
        field: 'avg_orders_per_month',
        headerName: t('table.avg_orders_per_month'),
        sortable: true,
        minWidth: 110,
        cellRenderer: ({ data }: { data: LegacyDashboardClient }) =>
          data ? <span className="text-sm text-title">{formatNumber(data.avg_orders_per_month, 2)}</span> : null,
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'sga_products_count',
        field: 'sga.products_count',
        headerName: t('table.sga_products_count'),
        sortable: true,
        minWidth: 130,
        cellRenderer: ({ data }: { data: LegacyDashboardClient }) => {
          if (!data?.sga) {
            return <span className="text-sm text-complementary-text">-</span>;
          }

          const count = data.sga.products_count;
          const hasDetail = count > 0 || (data.sga.products?.length ?? 0) > 0;

          if (!hasDetail || !onViewSgaProducts) {
            return <span className="text-sm text-title">{formatNumber(count)}</span>;
          }

          return (
            <Button
              type="button"
              variant="link"
              className="h-auto p-0 text-sm font-medium"
              onClick={() => onViewSgaProducts(data)}
              data-testid={`legacy-dashboard-sga-open-${data.legacy_team_id}`}
            >
              {formatNumber(count)}
            </Button>
          );
        },
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'sga_total_stock',
        field: 'sga.total_stock',
        headerName: t('table.sga_total_stock'),
        sortable: true,
        minWidth: 120,
        cellRenderer: ({ data }: { data: LegacyDashboardClient }) => {
          if (!data?.sga) {
            return <span className="text-sm text-complementary-text">-</span>;
          }

          const totalStock = data.sga.total_stock;
          const hasDetail = (data.sga.products?.length ?? 0) > 0;

          if (!hasDetail || !onViewSgaProducts) {
            return <span className="text-sm text-title">{formatNumber(totalStock)}</span>;
          }

          return (
            <Button
              type="button"
              variant="link"
              className="h-auto p-0 text-sm font-medium"
              onClick={() => onViewSgaProducts(data)}
              data-testid={`legacy-dashboard-sga-stock-open-${data.legacy_team_id}`}
            >
              {formatNumber(totalStock)}
            </Button>
          );
        },
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'is_migrated',
        field: 'is_migrated',
        headerName: t('table.is_migrated'),
        sortable: true,
        minWidth: 110,
        cellRenderer: ({ data }: { data: LegacyDashboardClient }) => {
          if (!data) return null;
          return (
            <Badge variant={data.is_migrated ? 'default' : 'outline'}>
              {data.is_migrated ? t('table.yes') : t('table.no')}
            </Badge>
          );
        },
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'new_child_team',
        headerName: t('table.new_child_team'),
        sortable: false,
        minWidth: 180,
        cellRenderer: ({ data }: { data: LegacyDashboardClient }) => {
          if (!data?.new_child_team_id) {
            return <span className="text-sm text-complementary-text">-</span>;
          }
          return (
            <Link
              to={`/clients/${data.new_child_team_id}`}
              className="text-sm font-medium text-primary hover:underline"
            >
              {data.new_child_team_name ?? data.new_child_team_id}
            </Link>
          );
        },
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'migration_run_status',
        field: 'migration_run_status',
        headerName: t('table.migration_run_status'),
        sortable: true,
        minWidth: 140,
        cellRenderer: ({ data }: { data: LegacyDashboardClient }) => {
          if (!data?.migration_run_status) {
            return <span className="text-sm text-complementary-text">-</span>;
          }
          const status = data.migration_run_status;
          return <Badge variant={MIGRATION_STATUS_VARIANT[status]}>{t(`migration_status.${status}`)}</Badge>;
        },
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'shop_channels',
        field: 'shop_channels',
        headerName: t('table.shop_channels'),
        sortable: false,
        minWidth: 150,
        cellRenderer: ({ data }: { data: LegacyDashboardClient }) => {
          if (!data?.shop_channels?.length) {
            return <span className="text-sm text-complementary-text">-</span>;
          }
          return <span className="text-sm text-title">{data.shop_channels.join(', ')}</span>;
        },
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'payment_profile',
        field: 'payment_profile',
        headerName: t('table.payment_profile'),
        sortable: true,
        minWidth: 120,
        cellRenderer: ({ data }: { data: LegacyDashboardClient }) => {
          if (!data?.payment_profile) return null;
          return (
            <Badge variant={PAYMENT_PROFILE_VARIANT[data.payment_profile]}>
              {t(`payment_profile.${data.payment_profile}`)}
            </Badge>
          );
        },
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'payment_breakdown',
        headerName: t('table.payment_breakdown'),
        sortable: false,
        minWidth: 200,
        cellRenderer: ({ data }: { data: LegacyDashboardClient }) => (
          <span className="text-sm text-title">{formatPaymentBreakdown(data?.payment_breakdown)}</span>
        ),
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'admin',
        headerName: t('table.admin_name'),
        sortable: false,
        minWidth: 180,
        cellRenderer: ({ data }: { data: LegacyDashboardClient }) =>
          data?.admin?.name ? (
            <UserCard name={data.admin.name} email={data.admin.email ?? ''} />
          ) : (
            <span className="text-sm text-complementary-text">-</span>
          ),
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'admin_email',
        headerName: t('table.admin_email'),
        sortable: false,
        minWidth: 200,
        cellRenderer: ({ data }: { data: LegacyDashboardClient }) =>
          data?.admin?.email ? (
            <span className="text-sm text-title">{data.admin.email}</span>
          ) : (
            <span className="text-sm text-complementary-text">-</span>
          ),
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'admin_phone',
        headerName: t('table.admin_phone'),
        sortable: false,
        minWidth: 130,
        cellRenderer: ({ data }: { data: LegacyDashboardClient }) =>
          data?.admin?.phone ? (
            <span className="text-sm text-title">{data.admin.phone}</span>
          ) : (
            <span className="text-sm text-complementary-text">-</span>
          ),
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'country_code',
        field: 'country_code',
        headerName: t('table.country_code'),
        sortable: true,
        minWidth: 90,
        cellRenderer: ({ data }: { data: LegacyDashboardClient }) =>
          data?.country_code ? (
            <span className="text-sm text-title">{data.country_code}</span>
          ) : (
            <span className="text-sm text-complementary-text">-</span>
          ),
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'verified',
        field: 'verified',
        headerName: t('table.verified'),
        sortable: true,
        minWidth: 100,
        cellRenderer: ({ data }: { data: LegacyDashboardClient }) => (
          <span className="text-sm text-title">{formatBool(data?.verified, t)}</span>
        ),
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'is_dropshipping',
        field: 'is_dropshipping',
        headerName: t('table.is_dropshipping'),
        sortable: true,
        minWidth: 120,
        cellRenderer: ({ data }: { data: LegacyDashboardClient }) => (
          <span className="text-sm text-title">{formatBool(data?.is_dropshipping, t)}</span>
        ),
        suppressHeaderMenuButton: true,
      },
      {
        colId: 'crm_active',
        field: 'crm_active',
        headerName: t('table.crm_active'),
        sortable: true,
        minWidth: 110,
        cellRenderer: ({ data }: { data: LegacyDashboardClient }) => (
          <span className="text-sm text-title">{formatBool(data?.crm_active, t)}</span>
        ),
        suppressHeaderMenuButton: true,
      },
    ],
    [t, onViewSgaProducts],
  );

  return { allColumnDefs };
};
